import React, { useEffect, useState } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  Timestamp,
  deleteDoc,
  doc,
  getDocs,
  getDoc
} from 'firebase/firestore';
import { db } from '@/services/firebase';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { User, MessageSquare, MoreVertical, Trash2, Users, ExternalLink } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { chatNotificationService } from '@/services/chatNotificationService';
import { useChatBar } from '@/context/ChatBarContext';

interface Chat {
  id: string;
  name?: string;
  participants: string[];
  lastMessage?: string;
  lastMessageSender?: string;
  updatedAt?: any;
  isPrivate?: boolean;
  createdBy?: string;
}

interface UserDisplayInfo {
  email: string;
  displayName: string;
  photoURL?: string;
}

interface FinChatsPanelProps {
  newChatEmail?: string;
  setNewChatEmail?: (email: string) => void;
  searchQuery?: string;
  shouldCreateChat?: boolean;
  selectedUserId?: string | null;
  onChatSelect?: (chatId: string) => void;
}

const FinChatsPanel: React.FC<FinChatsPanelProps> = ({ 
  newChatEmail, 
  setNewChatEmail,
  searchQuery = '',
  shouldCreateChat = false,
  selectedUserId = null,
  onChatSelect
}) => {
  const { currentUser } = useAuth();
  const { openChat, isChatOpen } = useChatBar();
  const [chats, setChats] = useState<Chat[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [menuOpenChatId, setMenuOpenChatId] = useState<string | null>(null);
  const [lastProcessedEmail, setLastProcessedEmail] = useState<string>('');
  const [deletingChatId, setDeletingChatId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [chatToDelete, setChatToDelete] = useState<{ id: string; chat: Chat } | null>(null);
  const [userDisplayInfo, setUserDisplayInfo] = useState<Map<string, UserDisplayInfo>>(new Map());
  const [unreadCounts, setUnreadCounts] = useState<Map<string, number>>(new Map());
  const [readChats, setReadChats] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('fin_chats_read');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch { return new Set(); }
  });
  const markChatRead = (chatId: string) => {
    setReadChats(prev => {
      const next = new Set(prev);
      next.add(chatId);
      try { localStorage.setItem('fin_chats_read', JSON.stringify([...next])); } catch {}
      return next;
    });
  };
  const navigate = useNavigate();

  // Function to fetch user display info
  const fetchUserDisplayInfo = async (email: string): Promise<UserDisplayInfo> => {
    if (userDisplayInfo.has(email)) {
      return userDisplayInfo.get(email)!;
    }

    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', email));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const userData = querySnapshot.docs[0].data();
        const displayInfo = {
          email,
          displayName: userData.displayName || email,
          photoURL: userData.photoURL
        };
        
        setUserDisplayInfo(prev => new Map(prev).set(email, displayInfo));
        return displayInfo;
      }
    } catch (error) {
      console.error('Error fetching user info:', error);
    }
    
    // Fallback
    const fallbackInfo = { email, displayName: email };
    setUserDisplayInfo(prev => new Map(prev).set(email, fallbackInfo));
    return fallbackInfo;
  };

  // Function to get display name for email
  const getDisplayName = (email: string): string => {
    return userDisplayInfo.get(email)?.displayName || email;
  };

  // Function to get photo URL for email
  const getPhotoURL = (email: string): string | undefined => {
    return userDisplayInfo.get(email)?.photoURL;
  };

  // Function to update unread counts for all chats
  const updateUnreadCounts = async (chats: Chat[], userEmail: string) => {
    try {
      const newUnreadCounts = new Map<string, number>();
      
      // Get unread counts for all chats using read receipts
      for (const chat of chats) {
        const unreadCount = await chatNotificationService.getUnreadCountWithReceipts(userEmail, chat.id);
        newUnreadCounts.set(chat.id, unreadCount);
      }
      
      setUnreadCounts(newUnreadCounts);
    } catch (error) {
      console.error('Error updating unread counts:', error);
      // Fallback to old method if needed
      const fallbackCounts = new Map<string, number>();
      chats.forEach(chat => {
        const count = chatNotificationService.getUnreadCount(userEmail, chat.id);
        fallbackCounts.set(chat.id, count);
      });
      setUnreadCounts(fallbackCounts);
    }
  };

  useEffect(() => {
    // Determine which user's chats to load
    const targetUserEmail = selectedUserId ? 
      (async () => {
        try {
          const userDoc = await getDoc(doc(db, 'users', selectedUserId));
          return userDoc.exists() ? userDoc.data().email : null;
        } catch (error) {
          console.error('Error fetching selected user email:', error);
          return null;
        }
      })() : 
      Promise.resolve(currentUser?.email);

    targetUserEmail.then(userEmail => {
      if (!userEmail) {
        console.log('❌ No user email available, skipping chat loading');
        return;
      }

      console.log('🔍 Starting chat loading for user:', userEmail, selectedUserId ? '(viewing as admin)' : '(own chats)');
      
      const userEmailLower = userEmail.toLowerCase();
    
    console.log('📧 Email formats to search:', { original: userEmail, lowercase: userEmailLower });

    // Create a more robust query strategy
    const queries = [
      query(collection(db, 'chats'), where('participants', 'array-contains', userEmail)),
      query(collection(db, 'chats'), where('participants', 'array-contains', userEmailLower))
    ];

    // Store unsubscribe functions
    const unsubscribes: (() => void)[] = [];
    
    // Combined chats map to handle deduplication
    const combinedChats = new Map<string, Chat>();
    let queryCompleted = 0;
    
    const updateChatsList = async () => {
      console.log('🔄 Updating chats list, total chats found:', combinedChats.size);
      
      const data = Array.from(combinedChats.values())
        .filter(chat => {
          // More robust participant checking
          const participants = chat.participants || [];
          const isParticipant = participants.includes(userEmail) || 
                              participants.includes(userEmailLower);
          
          console.log('🔍 Chat filtering:', {
            chatId: chat.id,
            participants: participants,
            isParticipant: isParticipant,
            chatName: chat.name,
            isPrivate: chat.isPrivate
          });
          
          return isParticipant;
        })
        .sort((a, b) => {
          const aTime = a.updatedAt?.seconds || 0;
          const bTime = b.updatedAt?.seconds || 0;
          return bTime - aTime;
        });

      console.log('✅ Filtered chats:', data.length);

      // Fetch display info for all participants
      const allParticipants = new Set<string>();
      data.forEach(chat => {
        chat.participants?.forEach(email => allParticipants.add(email));
      });
      
      // Fetch display info for participants we don't have yet
      const fetchPromises = Array.from(allParticipants)
        .filter(email => !userDisplayInfo.has(email))
        .map(email => fetchUserDisplayInfo(email));
      
      await Promise.allSettled(fetchPromises);
      setChats(data);
      
      // Update unread counts for all chats
      if (userEmail) {
        updateUnreadCounts(data, userEmail);
      }
    };

    // Set up listeners for each query
    queries.forEach((q, index) => {
      const unsubscribe = onSnapshot(q, (snapshot) => {
        console.log(`📥 Query ${index + 1} returned ${snapshot.docs.length} chats`);
        
        snapshot.docChanges().forEach(change => {
          if (change.type === 'modified') {
            const d = change.doc.data();
            const sender = d.lastMessageSender?.toLowerCase?.() || '';
            if (sender && sender !== userEmailLower) {
              setReadChats(prev => {
                if (!prev.has(change.doc.id)) return prev;
                const next = new Set(prev);
                next.delete(change.doc.id);
                try { localStorage.setItem('fin_chats_read', JSON.stringify([...next])); } catch {}
                return next;
              });
            }
          }
        });
        snapshot.docs.forEach(doc => {
          const chatData = { id: doc.id, ...doc.data() } as Chat;
          combinedChats.set(doc.id, chatData);
        });
        
        queryCompleted++;
        console.log(`🎯 Query ${index + 1} completed, total queries completed: ${queryCompleted}`);
        
        // Update the list after each query completes
        updateChatsList();
      }, (error) => {
        console.error(`❌ Query ${index + 1} error:`, error);
        console.error('Error details:', {
          code: error.code,
          message: error.message,
          userEmail: currentUser?.email || 'No user',
          userRoles: currentUser?.roles || 'No roles'
        });
        
        // Log errors but don't show toast notifications (can happen during logout)
        if (error.code === 'permission-denied') {
          console.error('🚫 Permission denied - check user roles and Firestore rules');
        } else {
          console.error('🚫 Chat loading error:', error.message);
        }
        console.log('🔓 Chat loading error occurred, user status:', currentUser?.email ? 'logged in' : 'logged out');
      });
      
      unsubscribes.push(unsubscribe);
    });

      // Cleanup function
      return () => {
        console.log('🧹 Cleaning up chat listeners');
        unsubscribes.forEach(unsub => unsub());
      };
    });
  }, [currentUser, selectedUserId]);

  // Handle new chat creation when explicitly requested
  useEffect(() => {
    const createNewChat = async () => {
      if (!currentUser || !newChatEmail?.trim() || !shouldCreateChat || isCreating) return;
      
      // Prevent processing the same email multiple times
      if (newChatEmail === lastProcessedEmail) return;
      
      setIsCreating(true);
      setLastProcessedEmail(newChatEmail);

      try {
        // Check if chat already exists
        const existingChat = chats.find(chat => 
          chat.isPrivate && 
          chat.participants.includes(currentUser.email) && 
          chat.participants.includes(newChatEmail.toLowerCase())
        );

        if (existingChat) {
          navigate(`/chats/${existingChat.id}`);
          toast.success('Opened existing chat');
          return;
        }

        // Ensure consistent email formatting
        const userEmail = currentUser.email.toLowerCase();
        const targetEmail = newChatEmail.trim().toLowerCase();
        const participants = [userEmail, targetEmail];
        
        const newChat = {
          participants,
          createdBy: currentUser.uid,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
          lastMessage: '',
          isPrivate: true
        };

        const docRef = await addDoc(collection(db, 'chats'), newChat);
        
        // Navigate to the new chat immediately
        navigate(`/chats/${docRef.id}`);
        toast.success('Chat created successfully');
      } catch (error) {
        console.error('Error creating chat:', error);
        toast.error('Failed to create chat');
      } finally {
        // Always clear the newChatEmail and reset creating state
        if (setNewChatEmail) setNewChatEmail('');
        setIsCreating(false);
        setLastProcessedEmail(''); // Reset this to allow future chats
      }
    };

    if (newChatEmail?.trim() && shouldCreateChat && newChatEmail !== lastProcessedEmail) {
      createNewChat();
    }
  }, [newChatEmail, shouldCreateChat, currentUser, navigate, setNewChatEmail, chats, isCreating, lastProcessedEmail]);

  const openDeleteConfirmation = (chatId: string, chat: Chat) => {
    if (!currentUser) return;
    
    // Only allow deletion if user created the chat or is a participant
    if (chat.createdBy !== currentUser.uid && !chat.participants.includes(currentUser.email)) {
      toast.error("You don't have permission to delete this chat");
      return;
    }

    setChatToDelete({ id: chatId, chat });
    setShowDeleteConfirm(true);
    setMenuOpenChatId(null); // Close the menu
  };

  const confirmDeleteChat = async () => {
    if (!chatToDelete) return;

    const { id: chatId, chat } = chatToDelete;
    setDeletingChatId(chatId);
    setShowDeleteConfirm(false);
    
    try {
      // Get the current URL to check if we're viewing the chat being deleted
      const currentPath = window.location.pathname;
      const isViewingThisChat = currentPath.includes(chatId);

      // Delete all messages in the chat first
      const messagesRef = collection(db, 'chats', chatId, 'messages');
      const messagesSnapshot = await getDocs(messagesRef);
      const deletePromises = messagesSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);

      // Delete the chat document
      await deleteDoc(doc(db, 'chats', chatId));
      
      // If we're currently viewing this chat, navigate away
      if (isViewingThisChat) {
        navigate('/chats');
      }
      
      // Update local state immediately for better UX
      setChats(prevChats => prevChats.filter(c => c.id !== chatId));
      
      toast.success('Chat deleted successfully');
    } catch (error) {
      console.error('Error deleting chat:', error);
      toast.error('Failed to delete chat');
    } finally {
      setDeletingChatId(null);
      setChatToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setChatToDelete(null);
  };

  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return '';
    const date = new Date(timestamp.seconds * 1000);
    return format(date, 'MMM d');
  };

  // Filter chats based on search query
  const filteredChats = chats.filter(chat => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    
    // Search in participant emails
    const participantMatch = chat.participants.some(p => 
      p.toLowerCase().includes(searchLower)
    );
    
    // Search in chat name
    const nameMatch = chat.name?.toLowerCase().includes(searchLower);
    
    // Search in last message
    const messageMatch = chat.lastMessage?.toLowerCase().includes(searchLower);
    
    return participantMatch || nameMatch || messageMatch;
  });

  return (
    <div className="py-2">
      {filteredChats.map((chat, index) => {
        const otherParticipants = chat.participants?.filter(e => e !== currentUser?.email) || [];
        const displayName = chat.name || otherParticipants.map(email => getDisplayName(email)).join(', ') || 'Unnamed Chat';
        const unreadCount = unreadCounts.get(chat.id) || 0;
        const hasNewMessage = !readChats.has(chat.id) && (
          chat.lastMessageSender
            ? chat.lastMessageSender.toLowerCase() !== currentUser?.email?.toLowerCase()
            : unreadCount > 0
        );
        
        return (
          <div
            key={chat.id}
            className={`group relative ${index !== filteredChats.length - 1 ? 'border-b border-gray-100' : ''}`}
          >
            <button
              onClick={() => {
                markChatRead(chat.id);
                if (onChatSelect) {
                  onChatSelect(chat.id);
                } else {
                  navigate(`/chats/${chat.id}`);
                }
              }}
              className={`w-full p-4 hover:bg-gray-50 transition-colors rounded-lg text-left
                       flex items-center gap-4 group relative ${
                         hasNewMessage
                           ? 'bg-[#faf6ee] border-l-[3px] border-l-[#b8986e]'
                           : ''
                       }`}
            >
              {/* Avatar */}
              <div className="flex-shrink-0 relative">
                {chat.isPrivate && otherParticipants.length === 1 ? (
                  // Show profile picture for 1-on-1 chats
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200">
                    {getPhotoURL(otherParticipants[0]) ? (
                      <img
                        src={getPhotoURL(otherParticipants[0])}
                        alt={getDisplayName(otherParticipants[0])}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                          if (nextElement) {
                            nextElement.style.display = 'flex';
                          }
                        }}
                      />
                    ) : null}
                    <div
                      className={`w-full h-full bg-[#EFE3C4] flex items-center justify-center text-[#3D2B1F] font-bold text-lg ${
                        getPhotoURL(otherParticipants[0]) ? 'hidden' : 'flex'
                      }`}
                    >
                      {getDisplayName(otherParticipants[0]).charAt(0).toUpperCase()}
                    </div>
                  </div>
                ) : (
                  // Show icon for group chats
                  <div className="w-12 h-12 rounded-full bg-[#EFE3C4] 
                                flex items-center justify-center">
                    <Users className="w-6 h-6 text-[#7A5A34]" />
                  </div>
                )}
                {/* Unread indicator */}
                {unreadCount > 0 && (
                  <div className="absolute -top-1 -right-1 min-w-[20px] h-5 bg-[#b8986e] text-white text-xs font-bold rounded-full flex items-center justify-center px-1.5 shadow-lg">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </div>
                )}
              </div>

              {/* Chat info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <h3 className={`${hasNewMessage ? 'font-bold' : 'font-medium'} text-gray-900 truncate`}>{displayName}</h3>
                  {chat.updatedAt && (
                    <span className="text-xs text-gray-400">{formatTimestamp(chat.updatedAt)}</span>
                  )}
                </div>
                <p className={`text-sm truncate ${hasNewMessage ? 'text-gray-700 font-medium' : 'text-gray-500'}`}>
                  {chat.lastMessage || 'No messages yet'}
                </p>
              </div>
            </button>

            {/* Action buttons */}
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1
                          opacity-0 group-hover:opacity-100 transition-opacity">
              {/* Open in IM Bar button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openChat(chat.id, {
                    displayName,
                    name: displayName,
                    photoURL: chat.isPrivate && otherParticipants.length === 1 
                      ? getPhotoURL(otherParticipants[0]) 
                      : null,
                    isGroup: !chat.isPrivate || otherParticipants.length > 1
                  });
                  toast.success(`${displayName} opened in chat bar`);
                }}
                className={`p-2 hover:bg-teal-100 rounded-full transition-colors ${
                  isChatOpen(chat.id) ? 'bg-teal-100 text-teal-600' : 'text-gray-400'
                }`}
                title="Open in chat bar"
              >
                <ExternalLink className="w-4 h-4" />
              </button>
              
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpenChatId(menuOpenChatId === chat.id ? null : chat.id);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <MoreVertical className="w-4 h-4 text-gray-400" />
                </button>

                {menuOpenChatId === chat.id && (
                  <>
                    {/* Backdrop to close menu */}
                    <div 
                      className="fixed inset-0 z-[9998]" 
                      onClick={() => setMenuOpenChatId(null)}
                    />
                    {/* Menu positioned above instead of below */}
                    <div className="absolute right-0 bottom-full mb-1 bg-white border border-gray-200 
                                  rounded-lg shadow-lg py-1 min-w-[120px] z-[9999]">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openDeleteConfirmation(chat.id, chat);
                        }}
                        disabled={deletingChatId === chat.id}
                        className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 
                                  transition-colors flex items-center gap-2 disabled:opacity-50 
                                  disabled:cursor-not-allowed"
                      >
                        <Trash2 className="w-4 h-4" />
                        {deletingChatId === chat.id ? 'Deleting...' : 'Delete Chat'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Hover indicator */}
            <div className="absolute right-12 top-1/2 -translate-y-1/2 w-1 h-8 rounded-full
                          bg-gradient-to-b from-teal-400 to-emerald-400 opacity-0 group-hover:opacity-100
                          transition-opacity" />
          </div>
        );
      })}

      {filteredChats.length === 0 && (
        <div className="px-4 py-8 text-center">
          <p className="text-gray-400 text-sm">
            {searchQuery ? 'No conversations found' : 'No conversations yet'}
          </p>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && chatToDelete && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-[400px] border border-gray-200 shadow-xl mx-4">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Delete Chat</h3>
                <p className="text-gray-500 text-sm">This action cannot be undone</p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 mb-2">
                Are you sure you want to delete this conversation?
              </p>
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <p className="text-gray-700 text-sm">
                  {chatToDelete.chat.name || 
                   chatToDelete.chat.participants?.filter(p => p !== currentUser?.email)
                     .map(email => getDisplayName(email)).join(', ') || 
                   'Unnamed Chat'}
                </p>
                <p className="text-gray-400 text-xs mt-1">
                  All messages will be permanently deleted
                </p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={cancelDelete}
                className="flex-1 py-2 px-4 rounded-lg border border-gray-300 text-gray-700 
                         hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteChat}
                disabled={!!deletingChatId}
                className="flex-1 py-2 px-4 rounded-lg bg-gradient-to-r from-red-500 to-red-600
                         text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed
                         hover:shadow-lg hover:shadow-red-500/20 transition-all"
              >
                {deletingChatId ? 'Deleting...' : 'Delete Chat'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinChatsPanel;
